import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Building2,
  Users,
  PackageOpen,
  ShieldCheck,
  PackageCheck,
  UserCog,
  Clock,
  ArrowUpRight,
  Wrench,
  Loader2,
} from "lucide-react";

/**
 * Global bakım modu anahtarı (şartname: sistem bakım modu).
 *
 * Asıl engelleme BACKEND'de: bakım modunda firma kullanıcılarının veri
 * değiştiren tüm API çağrıları 503 döner (bkz. backend/maintenance.py).
 * Bu kart yalnızca anahtarı yönetir. Master uçları etkilenmediği için bakım
 * modunu buradan kapatmak her zaman mümkündür.
 */
function MaintenanceCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master/system-settings");
        setEnabled(!!data.maintenance_mode);
        setMessage(data.maintenance_message || "");
      } catch (e) {
        notifyError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (nextEnabled, nextMessage) => {
    setSaving(true);
    try {
      const { data } = await api.put("/master/system-settings", {
        maintenance_mode: nextEnabled,
        maintenance_message: nextMessage,
      });
      setEnabled(!!data.maintenance_mode);
      setMessage(data.maintenance_message || "");
      toast.success(
        data.maintenance_mode ? "Bakım modu AÇILDI" : "Bakım modu kapatıldı"
      );
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className={`rounded-sm p-5 ${
        enabled ? "border-amber-500 bg-amber-500/10" : "border-border"
      }`}
      data-testid="maintenance-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-[240px] flex-1">
          <div
            className={`h-10 w-10 rounded-sm flex items-center justify-center shrink-0 ${
              enabled ? "bg-amber-500 text-foreground" : "bg-foreground text-background"
            }`}
          >
            <Wrench className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight">
              Sistem Bakım Modu
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Açıkken firma kullanıcıları kayıt ekleyemez/değiştiremez; kendi
              verilerini görmeye devam eder. Master Panel etkilenmez.
            </p>
          </div>
        </div>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider">
              {enabled ? "AÇIK" : "KAPALI"}
            </span>
            <Switch
              checked={enabled}
              disabled={saving}
              onCheckedChange={(v) => save(v, message)}
              data-testid="maintenance-toggle"
            />
          </div>
        )}
      </div>

      {!loading && (
        <div className="mt-4 border-t border-border pt-3 space-y-1.5">
          <Label className="label-eyebrow">Kullanıcıya Gösterilecek Mesaj</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Sistem bakım modunda. Lütfen daha sonra tekrar deneyiniz."
              className="rounded-sm flex-1 min-w-[240px]"
              data-testid="maintenance-message"
            />
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => save(enabled, message)}
              className="rounded-sm"
              data-testid="maintenance-message-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mesajı Kaydet"}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Boş bırakılırsa varsayılan metin kullanılır.
          </p>
        </div>
      )}
    </Card>
  );
}

function StatBlock({ label, value, icon: Icon, accent }) {
  return (
    <Card
      className="rounded-sm p-5 border-border"
      data-testid={`master-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div
          className={`h-9 w-9 rounded-sm flex items-center justify-center ${
            accent ? "bg-amber-500 text-foreground" : "bg-foreground text-background"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-4">
        {value}
      </div>
    </Card>
  );
}

export default function MasterDashboard() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.get("/master/stats"),
          api.get("/master/expiring-soon?days=14"),
        ]);
        setStats(s.data);
        setExpiring(e.data || []);
      } catch (err) {
        notifyError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="master-dashboard">
      <div>
        <div className="label-eyebrow text-amber-600">Master Panel</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-2">
          Platform Özeti
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          Tüm lastik oteli işletmelerinin canlı durumu.
        </p>
      </div>

      <MaintenanceCard />

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-sm" />
          ))}
        </div>
      ) : !stats ? (
        <Card className="rounded-sm border-border p-6 text-sm text-muted-foreground">
          İstatistikler yüklenemedi.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatBlock label="Toplam İşletme" value={stats.total_tenants} icon={Building2} accent />
            <StatBlock label="Aktif İşletme" value={stats.active_tenants} icon={ShieldCheck} />
            <StatBlock label="Askıdaki" value={stats.suspended_tenants} icon={ShieldCheck} />
            <StatBlock label="Toplam Kullanıcı" value={stats.total_users} icon={UserCog} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatBlock label="Toplam Müşteri" value={stats.total_customers} icon={Users} />
            <StatBlock label="Toplam Emanet" value={stats.total_storages} icon={PackageOpen} />
            <StatBlock label="Aktif Emanet" value={stats.active_storages} icon={PackageCheck} accent />
          </div>

          {/* Expiring soon widget */}
          <Card className="rounded-sm border-border" data-testid="expiring-soon-widget">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-amber-500 text-foreground flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="label-eyebrow text-amber-600">Yaklaşan</div>
                  <h3 className="font-display font-bold text-xl tracking-tight">
                    14 Gün İçinde Bitecek Abonelikler
                  </h3>
                </div>
              </div>
              <Link
                to="/master/abonelikler"
                className="text-sm font-semibold text-foreground hover:text-amber-600 inline-flex items-center gap-1"
              >
                Tümü <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            {expiring.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Yaklaşan abonelik bitişi yok.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {expiring.map((t) => {
                  const exp = t.subscription_expires_at
                    ? new Date(t.subscription_expires_at)
                    : null;
                  const days = exp
                    ? Math.max(0, Math.ceil((exp - Date.now()) / 86400000))
                    : 0;
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-4 p-4"
                      data-testid={`expiring-row-${t.id}`}
                    >
                      <div className="h-10 w-10 bg-foreground text-background flex items-center justify-center rounded-sm shrink-0">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{t.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {t.owner_email}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className={`text-xs font-bold uppercase tracking-wider ${
                            days <= 3 ? "text-destructive" : "text-amber-600"
                          }`}
                        >
                          {days} GÜN
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {exp ? exp.toLocaleDateString("tr-TR") : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="rounded-sm border-border bg-foreground text-background p-6 mt-6">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="label-eyebrow text-amber-400">İpucu</div>
                <div className="font-display font-bold text-lg tracking-tight mt-1">
                  Yeni bir lastik oteli abonelik aldığında "İşletmeler" sekmesinden tenant
                  oluşturun
                </div>
                <div className="text-sm text-background/60 mt-1">
                  Her işletme kendi izole verisi ve kullanıcılarıyla anında devreye girer.
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
